/**
 * @file planConfirm.ts
 * @brief What the plan confirmation step says before a card is charged, and
 *        when the page may call a plan "active".
 *
 * @details
 * The payments audit (16-10-2026, fix #13) found that one tap on "Choose" on
 * the phone charged the card with no confirmation, and "{plan} is now active"
 * showed without checking Stripe's answer. On the web the user always sees a
 * confirmation with the exact charge and interval ("Pay £17.00 now"), and the
 * success state shows only when the server (which re-reads Stripe on
 * `/subscriptions/sync`) returns status `active`.
 *
 * Prices are the server's (plan catalogue, minor units); nothing here knows a
 * price. The VAT wording is a switch (`vatLabel`), off until DES decides.
 * There is no "+VAT" mode (night E2E F3, 21-10-2026): VERBLIZR is not VAT
 * registered and every price, Business too, is the price paid.
 *
 * Pure (relative imports only) so node:test can load it.
 *
 * @author DES
 * @date 17-10-2026
 */

export type BillingInterval = "month" | "year";

/** The catalogue fields this file reads (GET /billing/subscriptions/catalog). */
export type CatalogPlan = {
  code: string;
  name: string;
  currency: string;
  priceMinor: number | null;
  annualPriceMinor?: number | null;
  annualMonthlyEquivalentMinor?: number | null;
  annualSavingPct?: number | null;
  includedMinutes?: number | null;
  includedInterpretationMinutes?: number | null;
  unlimitedNotes?: boolean;
  enterprise?: boolean;
  // Launch pricing (18-10-2026, proposed contract); absent on older backends.
  perSeat?: boolean | null;
  minSeats?: number | null;
  notesMinutes?: number | null;
  notesFairUseMinutes?: number | null;
  pooled?: boolean | null;
  overageRateMinor?: number | null;
  noPlanMinChargeMinor?: number | null;
  s2sRateMinor?: number | null;
  historyDays?: number | null;
  askLimit?: number | null;
  team?: boolean | null;
  // B20 / B43 (Free row): one-time mode; one pool for Notes or Full Intelligence.
  resetMode?: string | null;
  onePool?: boolean | null;
  /** B43 / W34: most of the pool Full Intelligence may use. */
  intelMaxMinutes?: number | null;
};

/** Free tier row (code "free", or a zero-priced non-Enterprise plan). */
export function isFreePlan(p: Pick<CatalogPlan, "code" | "priceMinor" | "enterprise">): boolean {
  return p.code === "free" || (p.priceMinor === 0 && !p.enterprise);
}

/** "incl. VAT" after a price, or nothing (the default). */
export type VatLabelMode = "" | "incl";

export function parseVatLabelMode(raw: string | null | undefined): VatLabelMode {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "incl" || v === "inclusive" || v === "incl. vat") return "incl";
  return "";
}

export function vatSuffix(mode: VatLabelMode): string {
  if (mode === "incl") return " incl. VAT";
  return "";
}

export function formatMinor(minor: number, currency: string): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: (currency || "GBP").toUpperCase(),
    // "$18.99" for a US customer, not en-GB's "US$18.99" (18-10-2026).
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

/** The charge for one interval, or null when that interval is not offered. */
export function planChargeMinor(plan: CatalogPlan, interval: BillingInterval): number | null {
  if (plan.enterprise) return null;
  const v = interval === "year" ? plan.annualPriceMinor : plan.priceMinor;
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;
}

export function intervalOffered(plan: CatalogPlan, interval: BillingInterval): boolean {
  return planChargeMinor(plan, interval) != null;
}

export type PlanConfirmation = {
  /** "Pro, monthly" */
  heading: string;
  /** The button: "Pay £17.00 now" */
  payLabel: string;
  /** "Then £17.00 every month until you cancel." */
  renewLine: string;
  chargeMinor: number;
  interval: BillingInterval;
};

/**
 * The confirmation for a plan + interval, or null when it cannot be bought
 * that way (Enterprise, or an interval the server does not offer).
 */
export function buildPlanConfirmation(
  plan: CatalogPlan,
  interval: BillingInterval,
  vat: VatLabelMode = "",
): PlanConfirmation | null {
  const charge = planChargeMinor(plan, interval);
  if (charge == null) return null;
  const money = `${formatMinor(charge, plan.currency)}${vatSuffix(vat)}`;
  return {
    heading: `${plan.name}, ${interval === "year" ? "yearly" : "monthly"}`,
    payLabel: `Pay ${formatMinor(charge, plan.currency)} now`,
    renewLine: `Then ${money} every ${interval === "year" ? "year" : "month"} until you cancel.`,
    chargeMinor: charge,
    interval,
  };
}

/** Stripe statuses as the server maps them (subscription.service mapStripeStatus). */
export type SubscribeOutcome =
  | { kind: "active" }
  | { kind: "needs_action"; clientSecret: string }
  | { kind: "pending"; status: string }
  | { kind: "failed"; status: string };

/**
 * What to do with the answer from POST /billing/subscriptions. Only `active`
 * (or `trialing`, which the server also counts as entitled) is success;
 * `requires_action` with a client secret means 3D Secure on the first payment.
 */
export function subscribeOutcome(res: {
  status?: string | null;
  clientSecret?: string | null;
}): SubscribeOutcome {
  const status = String(res.status ?? "").toLowerCase();
  if (res.clientSecret) return { kind: "needs_action", clientSecret: res.clientSecret };
  if (status === "active" || status === "trialing") return { kind: "active" };
  if (status === "incomplete" || status === "processing" || status === "") {
    return { kind: "pending", status: status || "unknown" };
  }
  return { kind: "failed", status };
}

/** True only when the server's own record says the plan is live. */
export function isServerActive(status: string | null | undefined): boolean {
  const s = String(status ?? "").toLowerCase();
  return s === "active" || s === "trialing";
}

// ----------------------------------------------------------------------
// Plan change (BACKEND A2, 18-10-2026): POST /billing/subscriptions/change
// switches plan or interval with proration (`always_invoice`): moving up
// charges the difference for the rest of the period now; moving down credits
// the unused part against the next payments. The server only knows the exact
// proration once Stripe invoices it, so the page states the rule, not a figure.
// ----------------------------------------------------------------------

/** A month's worth of a charge, to compare a monthly with a yearly price. */
function perMonth(minor: number, interval: BillingInterval): number {
  return interval === "year" ? minor / 12 : minor;
}

export type ChangeConfirmation = {
  heading: string;
  /** The button: "Switch to Business" */
  actionLabel: string;
  direction: "up" | "down" | "same";
  /** What happens to money now. */
  nowLine: string;
  /** "Then £25.00 every month until you cancel." */
  renewLine: string;
};

export function buildChangeConfirmation(
  from: { plan: CatalogPlan; interval: BillingInterval },
  to: CatalogPlan,
  interval: BillingInterval,
  vat: VatLabelMode = "",
): ChangeConfirmation | null {
  const toCharge = planChargeMinor(to, interval);
  const fromCharge = planChargeMinor(from.plan, from.interval);
  if (toCharge == null) return null;
  if (from.plan.code === to.code && from.interval === interval) return null;
  const a = fromCharge == null ? 0 : perMonth(fromCharge, from.interval);
  const b = perMonth(toCharge, interval);
  const direction: ChangeConfirmation["direction"] =
    interval !== from.interval && from.plan.code === to.code
      ? interval === "year" ? "up" : "down"
      : b > a ? "up" : b < a ? "down" : "same";
  const every = interval === "year" ? "year" : "month";
  return {
    heading: `${to.name}, ${interval === "year" ? "yearly" : "monthly"}`,
    actionLabel: from.plan.code === to.code ? `Switch to ${every === "year" ? "yearly" : "monthly"}` : `Switch to ${to.name}`,
    direction,
    nowLine:
      direction === "up"
        ? "You pay the difference for the rest of this period now. Stripe works it out from the days left."
        : direction === "down"
          ? "The unused part of this period is credited against your next payments. Nothing is charged now."
          : "Nothing extra is charged now.",
    renewLine: `Then ${formatMinor(toCharge, to.currency)}${vatSuffix(vat)} every ${every} until you cancel.`,
  };
}

/** The change is done only when the server reports the new plan, active, nothing pending. */
export function changeOutcome(
  res: { clientSecret?: string | null; pending?: boolean; subscription?: { planCode?: string; status?: string; billingInterval?: string | null } | null },
  target: { planCode: string; interval: BillingInterval },
):
  | { kind: "done" }
  | { kind: "needs_action"; clientSecret: string }
  | { kind: "pending" } {
  if (res.clientSecret) return { kind: "needs_action", clientSecret: res.clientSecret };
  const sub = res.subscription;
  const interval = sub?.billingInterval === "year" ? "year" : "month";
  if (!res.pending && sub && sub.planCode === target.planCode && interval === target.interval && isServerActive(sub.status)) {
    return { kind: "done" };
  }
  return { kind: "pending" };
}

/**
 * The exact money line for a change when the server previewed it (18-10-2026):
 * "You pay £7.79 now for the rest of this period." / a credit / nothing.
 * Null when there is no preview (the rule-based nowLine stays).
 */
export function changeMoneyLine(
  preview: { amountDueCents: number; creditCents?: number | null; currency: string } | null | undefined,
): { line: string; payLabel: string | null } | null {
  if (!preview || !Number.isFinite(preview.amountDueCents)) return null;
  if (preview.amountDueCents > 0) {
    const money = formatMinor(preview.amountDueCents, preview.currency);
    return { line: `You pay ${money} now for the rest of this period.`, payLabel: `Pay ${money} now` };
  }
  const credit = preview.creditCents ?? 0;
  if (credit > 0) {
    return {
      line: `${formatMinor(credit, preview.currency)} is credited against your next payments. Nothing is charged now.`,
      payLabel: null,
    };
  }
  return { line: "Nothing is charged now.", payLabel: null };
}
